import React, {forwardRef, useImperativeHandle, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import Loading from "../../components/loading";
import {Form, Formik} from "formik";
import {addressRequestSchema} from "../../utils/schemas";
import FormGroup from "../../components/form-group";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {useGlobalNotificationMessage} from "../../context/message";
import {AddressTypes} from "../../utils/constants";
import {useAddressService} from "../../services/address-service";
import {useCountryService} from "../../services/country-service";

const AddEditAddressModal = forwardRef( ({onSuccess=null, ...props}, ref) => {
  const [show, setShow] = useState(false);
  const [model, setModel] = useState();
  const [loading, setLoading] = useState(false);
  const [countriesList, setCountriesList] = useState([]);
  const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();
  const [reset, setReset] = useState(0);

  let formSchema = addressRequestSchema;

  let addressService = useAddressService();
  let countryService = useCountryService();

  //expose api for interacting with component
  useImperativeHandle(ref, () => ({
    openModal: openModal,
    closeModal: closeModal,
  }));

  const openModal = (model=null, morphModel, morphId, type= AddressTypes.default.val) => {
    setLoading(true);
    if (!morphId || !morphModel){
      throw new Error('No morph info');
    }

    let promises = [
      Promise.resolve({addressable_type: morphModel, addressable_id: morphId}),
      countryService.getList()
    ];

    if (model && model.id){
      promises.push(getDetail(model.id));
    }

    Promise.all(promises).then(([morphData={}, countriesList=[], detail= {}])=>{
      setCountriesList(countriesList);
      //pre-select type
      let newDetail = Object.assign({},{type: type}, detail, morphData);
      setModel(formSchema.cast(newDetail));
      setLoading(false);
    });

    setShow(true);
  }
  const closeModal = () => {
    setShow(false);
    setReset(reset+1);
    setModel(formSchema.cast());
  }

  const getDetail = (id) => {
    return addressService.getDetail(id);
  }

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    removeMessage();
    let reqValues = {...values};

    addressService.addEditModel(reqValues, reqValues.id)
      .then((res) => {
        if (onSuccess && 'function' === typeof onSuccess){
          onSuccess(res);
        }
        closeModal();
      })
      .catch(error => {
        HelperService.handleApiErrors(error, addErrorMessage, "Nepodarilo sa uložiť zmeny.");
        HelperService.handleFormErrors(error, setErrors);
      })
      .finally(()=>{ setSubmitting(false);});
  };

  return (
    <>
      <Formik
        key={reset}
        enableReinitialize={true}
        initialValues={formSchema.cast(model) || formSchema.cast()}
        validationSchema={formSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, handleSubmit, setFieldValue}) => (

          <Modal size="lg" show={!!show} onHide={closeModal} centered>
            <Modal.Header closeButton>
              { model && <Modal.Title>{ !model.id ? 'Nová adresa' : 'Úprava adresy' }</Modal.Title>}
            </Modal.Header>
            <Modal.Body>
              <Section animation={HelperService.getRandomAnimation()}>

                {loading && <Loading className="m-2" />}

                {!loading && <Form className="row">
                  <FormGroup
                    name="type"
                    type="select"
                    options={Object.values(AddressTypes)}
                    optionLabel="title"
                    optionKey="val"
                    placeholder="typ"
                    label="Typ"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="title"
                    placeholder={'Názov'}
                    label="Názov"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className="clearfix"></div>
                  <hr />

                  <FormGroup
                    name="street"
                    placeholder="Ulica"
                    label="Ulica"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className={"col-md-4"}>
                    <FormGroup
                        name="street_plan_number"
                        placeholder="2024"
                        label="Súpisné číslo"
                        className="col"
                        touched={touched} errors={errors} schema={formSchema}
                    />
                    /
                    <FormGroup
                        name="street_number"
                        placeholder="3"
                        label="Orientačné číslo"
                        className="col"
                        touched={touched} errors={errors} schema={formSchema}
                    />
                  </div>

                  <FormGroup
                    name="city"
                    placeholder="Mesto"
                    label="Mesto"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="zip"
                    placeholder="PSČ"
                    label="PSČ"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <FormGroup
                    name="country_id"
                    type="select"
                    options={countriesList}
                    optionLabel="title"
                    placeholder="Krajina"
                    label="Krajina"
                    className="col-md-4"
                    touched={touched} errors={errors} schema={formSchema}
                  />

                  <div className="clearfix"></div>

                </Form>}

              </Section>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeModal}>Zatvoriť</Button>
              <Button type="submit" variant="primary" onClick={handleSubmit} disabled={isSubmitting}>Odoslať</Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>

    </>
  );
});

export default AddEditAddressModal;
